/*
 * a11y-modal.js — shared keyboard-accessibility helpers for modal dialogs.
 * P-06 / P-07: focus management for the "Compare" pop-up and the product-image
 * lightbox (Luminous). Vanilla JS, no dependencies. Loaded once site-wide.
 *
 * Public API:
 *   window.a11yTrapFocus(modalEl, triggerEl)
 *     - Remembers the trigger (defaults to the currently focused element),
 *       moves focus into the modal, and traps Tab / Shift+Tab inside it.
 *   window.a11yReleaseFocus()
 *     - Removes the trap and returns focus to the remembered trigger.
 *
 * Escape-to-close is intentionally left to each modal's own code (the compare
 * modals and Luminous already handle Escape); this helper only manages focus.
 */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])', 'iframe',
    'audio[controls]', 'video[controls]', '[contenteditable]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var _trigger = null;
  var _modal = null;
  var _handler = null;

  function focusable(container) {
    return Array.prototype.slice
      .call(container.querySelectorAll(FOCUSABLE))
      // visible only (offsetParent is null for display:none / detached)
      .filter(function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
  }

  window.a11yTrapFocus = function (modal, trigger) {
    if (!modal) return;
    // Clean up any previous trap without bouncing focus back to its trigger.
    if (_handler) {
      document.removeEventListener('keydown', _handler, true);
      _handler = null;
    }
    _modal = modal;
    _trigger = trigger || document.activeElement;

    var items = focusable(modal);
    if (items.length) {
      items[0].focus();
    } else {
      // Nothing focusable inside — make the dialog itself focusable.
      if (!modal.hasAttribute('tabindex')) modal.setAttribute('tabindex', '-1');
      modal.focus();
    }

    _handler = function (e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      var f = focusable(modal);
      if (!f.length) { e.preventDefault(); return; }
      var first = f[0];
      var last = f[f.length - 1];
      var active = document.activeElement;
      if (!modal.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', _handler, true);
  };

  window.a11yReleaseFocus = function () {
    if (_handler) {
      document.removeEventListener('keydown', _handler, true);
      _handler = null;
    }
    var trigger = _trigger;
    _trigger = null;
    _modal = null;

    // Prefer returning focus to the trigger — but only if it is still
    // focusable (visible & attached). On pages with duplicate/hidden compare
    // triggers the original can be display:none by now, and focusing it is a
    // no-op that would strand focus on the just-hidden modal.
    if (trigger && typeof trigger.focus === 'function' &&
        trigger.isConnected && trigger.offsetParent !== null) {
      try { trigger.focus(); } catch (e) {}
    }
    // Safety net: if focus did not actually move to the trigger, send it to a
    // sensible landmark so it never gets lost on the now-hidden dialog.
    if (!trigger || document.activeElement !== trigger) {
      var fallback = document.getElementById('main') ||
                     document.querySelector('main, [role="main"]') ||
                     document.body;
      if (fallback) {
        if (!fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
        try { fallback.focus(); } catch (e) {}
      }
    }
  };

  /* -------------------------------------------------------------------------
   * Luminous image lightbox (P-07).
   * Luminous builds its overlay (.lum-lightbox) dynamically and adds no ARIA
   * or focus management. It already closes on Escape (closeWithEscape default
   * true). We watch for the overlay opening/closing and layer accessibility on
   * top — this covers every `new Luminous()` / `new LuminousGallery()` instance
   * site-wide without editing each section.
   * ---------------------------------------------------------------------- */
  function enhanceButton(btn, label) {
    if (!btn) return;
    btn.setAttribute('role', 'button');
    if (!btn.getAttribute('aria-label')) btn.setAttribute('aria-label', label);
    if (!btn.hasAttribute('tabindex')) btn.setAttribute('tabindex', '0');
    if (!btn.dataset.a11yKey) {
      btn.dataset.a11yKey = '1';
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.keyCode === 13 || e.keyCode === 32) {
          e.preventDefault();
          btn.click();
        }
      });
    }
  }

  var openLightbox = null;

  function onLightboxOpen(el) {
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', 'Image viewer');
    enhanceButton(el.querySelector('.lum-close-button'), 'Close image viewer');
    enhanceButton(el.querySelector('.lum-previous-button'), 'Previous image');
    enhanceButton(el.querySelector('.lum-next-button'), 'Next image');
    // Trigger = the thumbnail/link that was focused when the lightbox opened.
    window.a11yTrapFocus(el, document.activeElement);
  }

  function checkLightbox() {
    var el = document.querySelector('.lum-lightbox.lum-open');
    if (el && el !== openLightbox) {
      openLightbox = el;
      onLightboxOpen(el);
    } else if (!el && openLightbox) {
      openLightbox = null;
      window.a11yReleaseFocus();
    }
  }

  /* -------------------------------------------------------------------------
   * Compare Products modal.
   * Both compare snippets open the modal from several places — modalShow(),
   * buildHtmlCompare(), and the per-card COMPARE buttons all add .showModal —
   * so wiring the trap to any one entry point misses the others (live testing
   * on /collections/reels caught exactly that: opening from a product card
   * left focus outside the dialog, on the cookie banner). Watching the class
   * that actually opens it covers every path in both snippets at once.
   * ---------------------------------------------------------------------- */
  var openCompare = null;
  var compareTrigger = null;

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var opener = t.closest('.compareProduct, .compareVariant button, .compareMobile');
    if (opener) compareTrigger = opener;
  }, true);

  /* REEL-169 / APCL-75: the modal shows a spinner while it fetches each product,
     but said nothing — so a screen reader user heard silence between opening it
     and the cards appearing. */
  function compareStatus(el) {
    var region = el.querySelector(':scope > .a11y-compare-status');
    if (!region) {
      region = document.createElement('p');
      region.className = 'a11y-compare-status visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      el.appendChild(region);
    }
    return region;
  }

  function watchCompareLoading(el) {
    var region = compareStatus(el);
    var container = el.querySelector('#compareContainer');
    if (!container) return;
    /* Only claim to be loading when there is genuinely nothing there yet —
       reopening the dialog must not overwrite a count already announced. */
    if (!container.querySelectorAll('[role="listitem"]').length) {
      region.textContent = 'Loading comparison\u2026';
    }

    /* Cards arrive one fetch at a time, and more can be added while the dialog is
       already open, so a one-shot check reports a count that is still climbing.
       Watch the container and re-announce once it has been still for a moment. */
    if (!('MutationObserver' in window)) return;
    if (container.dataset.a11yCompareWatch === '1') return;
    container.dataset.a11yCompareWatch = '1';

    var settle = null;
    new MutationObserver(function () {
      window.clearTimeout(settle);
      settle = window.setTimeout(function () {
        var cards = container.querySelectorAll('[role="listitem"]').length;
        if (!cards) return;
        region.textContent = cards === 1
          ? '1 product ready to compare.'
          : cards + ' products ready to compare.';
      }, 700);
    }).observe(container, { childList: true, subtree: true });
  }

  function checkCompare() {
    var el = document.querySelector('.compareModal.showModal');
    if (el && el !== openCompare) {
      openCompare = el;
      window.a11yTrapFocus(el, compareTrigger || document.activeElement);
      watchCompareLoading(el);
    } else if (!el && openCompare) {
      openCompare = null;
      compareTrigger = null;
      window.a11yReleaseFocus();
    }
  }

  function check() {
    checkLightbox();
    checkCompare();
  }

  /* -------------------------------------------------------------------------
   * In-page section navigation (SEAB-54, TATU-94).
   * Activating a link in the product quick-nav scrolled the page but left focus
   * on the link, so the next Tab carried on from the nav rather than from the
   * section that had just arrived. There are six copies of this nav across the
   * product templates, so it is handled once here rather than in each of them.
   * ---------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var link = e.target.closest ? e.target.closest('.prod-nav a[href^="#"]') : null;
    if (!link) return;
    var id = link.getAttribute('href').slice(1);
    if (!id) return;
    var target = document.getElementById(id);
    if (!target) return;
    window.setTimeout(function () {
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }
    }, 400);
  });


  /* ------------------------------------------------------------------
   * HOME-25 — dismissing the mega-menu with Escape.
   *
   * Timber's accessibleNav opens a dropdown when its parent link takes focus:
   * handleFocus() adds .nav-focus and the CSS rule
   *     .nav-focus + .site-nav--dropdown { opacity:1; pointer-events:auto }
   * reveals it. Nothing closes it again, so a keyboard user landing on REELS
   * must tab all nine children to reach RODS. Escape did nothing — there was
   * no keydown handler at all, confirmed with a real keypress.
   *
   * Two things make the naive fix fail, both learned the hard way:
   *   1. Removing .nav-focus does not hold. Timber re-adds it asynchronously
   *      when focus returns to the parent link, so the panel springs back.
   *   2. opacity/pointer-events do not affect the tab order. Hiding the panel
   *      visually still leaves its nine links tabbable.
   * So instead of fighting Timber we add our own class that outranks it, and
   * take the children out of the tab sequence explicitly. The dismissal is
   * cleared as soon as focus leaves the menu, so the next visit behaves
   * normally. What OPENS the panel is deliberately unchanged — rebuilding the
   * nav as an activation-gated disclosure would touch every page on the site.
   * ------------------------------------------------------------------ */
  var NAV_PARENT = '.site-nav--has-dropdown';
  var NAV_LINK = '.site-nav--link';
  var NAV_DISMISSED = 'a11y-nav-dismissed';

  function injectNavStyle() {
    if (document.getElementById('a11y-nav-style')) return;
    var st = document.createElement('style');
    st.id = 'a11y-nav-style';
    /* !important so it beats Timber's .nav-focus rule whenever that reappears */
    st.textContent =
      '.' + NAV_DISMISSED + ' > .site-nav--dropdown,' +
      '.' + NAV_DISMISSED + ' > .site-nav--dropdown-grandchild{' +
      'opacity:0 !important;pointer-events:none !important;}';
    document.head.appendChild(st);
  }

  function navChildren(parent) {
    return parent.querySelectorAll('.site-nav--dropdown ' + NAV_LINK +
      ', .site-nav--dropdown-grandchild ' + NAV_LINK);
  }

  function dismissNav(parent) {
    parent.classList.add(NAV_DISMISSED);
    parent.classList.remove('nav-hover');
    var kids = navChildren(parent);
    for (var i = 0; i < kids.length; i++) kids[i].setAttribute('tabindex', '-1');
    var top = parent.querySelector(NAV_LINK);
    if (top) top.setAttribute('aria-expanded', 'false');
    return top;
  }

  function restoreNav(parent) {
    if (!parent.classList.contains(NAV_DISMISSED)) return;
    parent.classList.remove(NAV_DISMISSED);
    var kids = navChildren(parent);
    for (var i = 0; i < kids.length; i++) kids[i].removeAttribute('tabindex');
  }

  function onNavKeydown(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    /* never steal Escape from an open lightbox or dialog — REEL-174 was this
       same bug in reverse, one press collapsing two layers at once */
    if (document.querySelector('.lum-lightbox.lum-open')) return;
    /* presence is not openness: the wishlist dialog sits in the DOM on every
       page, so this must test that one is actually rendered, not that one
       exists. Getting this wrong silently disabled the whole handler. */
    var dlgs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
    for (var d = 0; d < dlgs.length; d++) {
      if (dlgs[d].getBoundingClientRect().height > 0) return;
    }

    var active = document.activeElement;
    var parent = active && active.closest && active.closest(NAV_PARENT);
    if (!parent) return;

    e.preventDefault();
    e.stopPropagation();

    /* Order matters. Timber's handleFocus runs $allLinks.attr('tabindex','')
       on every focus, so anything set before moving focus gets wiped. Focus
       the parent first, let Timber do its pass, then dismiss — otherwise the
       panel hides but its nine links stay in the tab order. */
    var top = parent.querySelector(NAV_LINK);
    if (top && top !== active) top.focus();
    dismissNav(parent);
  }

  /* Leaving the menu resets it, so Escape only affects the visit it was used in. */
  function onNavFocusOut(e) {
    var parent = e.target && e.target.closest && e.target.closest(NAV_PARENT);
    if (!parent) return;
    window.setTimeout(function () {
      if (!parent.contains(document.activeElement)) restoreNav(parent);
    }, 0);
  }

  function onNavPointerEnter(e) {
    var parent = e.target && e.target.closest && e.target.closest(NAV_PARENT);
    if (parent) restoreNav(parent);
  }

  function startNavEscape() {
    if (!document.querySelector(NAV_PARENT)) return;
    injectNavStyle();
    document.addEventListener('keydown', onNavKeydown, true);
    document.addEventListener('focusout', onNavFocusOut, true);
    document.addEventListener('mouseenter', onNavPointerEnter, true);
  }

  function startObserver() {
    if (!('MutationObserver' in window)) return;
    var obs = new MutationObserver(check);
    obs.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function boot() {
    startNavEscape();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
